import type { CameraGraph } from '@toolproof-core/visualization/_contract/camera';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import type { PrimitiveCameraConfig } from '@/adapters/primitive/_contract/camera';
import type { Config } from '@/adapters/primitive/_contract/config';

export function toPrimitiveCameraConfig(config: Config): PrimitiveCameraConfig {
    return { base: config.base.camera, boolean: config.boolean.camera, natural: config.natural.camera };
}

/** Keep Natural working stations wide enough for the outer ring and its longest title flag. */
export function fitPrimitiveCameraOffsetToWheelPower(args: {
    config: Config;
    offset: [number, number, number];
    maximumPower: number;
    fov: number;
    padding: number;
}): [number, number, number] {
    const { config, offset, maximumPower, fov, padding } = args;
    if (!Number.isInteger(maximumPower) || maximumPower < 0
        || !Number.isFinite(fov) || fov <= 0 || fov >= 180
        || !Number.isFinite(padding) || padding < 1
        || !offset.every(Number.isFinite) || Math.hypot(...offset) === 0) {
        throw new Error('Primitive camera power fitting requires a nonnegative power and valid camera geometry.');
    }
    const { innerRadius, ringWidth } = config.base.display.wheel;
    const ringGap = config.natural.display.wheel.ringGap;
    const label = config.base.display.label;
    const flagExtent = label.wheelTitlePresentationMode === 'text' ? 0
        : (label.aboveItemStyleOverride?.fontSize ?? label.wheelTitleFontSize)
            * (5 + label.wheelTitleFlagClearanceFontRatio) + label.wheelTitlePadding;
    const outerRadius = innerRadius + maximumPower * (ringWidth + ringGap) + ringWidth + flagExtent;
    const minimumDistance = outerRadius * padding / Math.sin(fov * Math.PI / 360);
    const currentDistance = Math.hypot(...offset);
    const scale = Math.max(1, minimumDistance / currentDistance);
    return [offset[0] * scale, offset[1] * scale, offset[2] * scale];
}

export function toPrimitiveCameraViewId(scope: string, focus: string): string {
    return `primitive-camera:${encodeURIComponent(scope)}:${focus}`;
}


/** Pairs a Natural working station with its Composite-settlement station sharing the same receiver anchor. */
export function toPrimitiveStandoffStationId(workingStationId: string): string {
    if (!workingStationId.includes(':working:')) throw new Error(`Not a Natural working station: ${workingStationId}`);
    return workingStationId.replace(':working:', ':standoff:');
}


export function withPrimitiveCameraDestination(
    frame: SpaceDerivationFrame, graph: CameraGraph, activeView: string | undefined, camera: PrimitiveCameraConfig,
): SpaceDerivationFrame {
    return {
        ...frame,
        spaceRenderOptions: { ...frame.spaceRenderOptions, camera: {
            ...frame.spaceRenderOptions?.camera,
            graph: { ...graph, paths: activeView === undefined ? [] : Object.keys(graph.views).filter((from) => from !== activeView)
                .map((from) => ({ pathId: `${from}->${activeView}`, from, to: activeView, pathKind: 'straight' as const })) },
            activeView, navigationMode: camera.base.navigationMode, navigationDurationMs: camera.base.navigationDurationMs,
        } },
    };
}
